// ts-rest contract for the operational-analytics ingest endpoint (issue #343 /
// ADR-0020) — the single typed event shape shared by the browser client, the
// server ingest, and the `activity_events` store.
//
// Trust boundary: the client supplies only event CONTENT (kind, path, the
// interaction details). It NEVER supplies identity — actor / persona / ip /
// user-agent are re-derived server-side from the session at ingest, and any
// client-claimed identity is ignored. To make that boundary structural, the
// ingest payload only accepts the two CLIENT-emitted kinds (`page_view`,
// `interaction`); the server-only kinds (`api_request`, `read_access`, `auth`)
// cannot be injected by a client.
//
// Endpoint under /api/analytics:
//   POST /api/analytics/ingest — accept a batch of client-emitted events

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Event kinds
// ---------------------------------------------------------------------------

/** Every capture kind in the store (server + client sources). The store truth. */
export const ANALYTICS_EVENT_KINDS = [
    'page_view',
    'interaction',
    'api_request',
    'read_access',
    'auth',
] as const;
export const analyticsEventKindSchema = z.enum(ANALYTICS_EVENT_KINDS);
export type AnalyticsEventKind = z.infer<typeof analyticsEventKindSchema>;

/** The subset a browser client is allowed to emit (the rest are server-sourced). */
export const analyticsClientEventKindSchema = z.enum(['page_view', 'interaction']);
export type AnalyticsClientEventKind = z.infer<typeof analyticsClientEventKindSchema>;

// ---------------------------------------------------------------------------
// Client event + batch
// ---------------------------------------------------------------------------

/**
 * A single browser-emitted event. CONTENT only — no identity fields. `details`
 * is a small free-form bag for interaction specifics (e.g. the search term, the
 * persona switched to, the export template). Bounded so a client cannot push an
 * unbounded blob into the compliance stream.
 */
export const analyticsClientEventSchema = z.object({
    kind: analyticsClientEventKindSchema,
    // Client/source event time (ISO). Untrusted — recorded as `occurredAt`; the
    // server stamps the authoritative `receivedAt`.
    occurredAt: z.string().min(1).max(40),
    // Client-generated visit correlation id (sessionStorage-backed).
    sessionId: z.string().min(1).max(80),
    // Navigation dimensions.
    path: z.string().max(2048).optional(),
    route: z.string().max(512).optional(),
    // Optional subject of an interaction (e.g. a client record opened).
    objectType: z.string().max(120).optional(),
    objectId: z.string().max(120).optional(),
    details: z.record(z.unknown()).optional(),
});
export type AnalyticsClientEvent = z.infer<typeof analyticsClientEventSchema>;

export const analyticsIngestRequestSchema = z.object({
    events: z.array(analyticsClientEventSchema).min(1).max(100),
});
export type AnalyticsIngestRequest = z.infer<typeof analyticsIngestRequestSchema>;

export const analyticsIngestResponseSchema = z.object({
    ok: z.literal(true),
    accepted: z.number(),
});
export type AnalyticsIngestResponse = z.infer<typeof analyticsIngestResponseSchema>;

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const analyticsContract = c.router(
    {
        ingest: {
            method: 'POST',
            path: '/analytics/ingest',
            body: analyticsIngestRequestSchema,
            responses: {
                200: analyticsIngestResponseSchema,
                400: errorBody,
            },
            summary:
                'Ingest a batch of client-emitted analytics events (identity re-derived server-side)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
